import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// The supervisory record (E09-S6-T4), ported from record() in mockups/kit/components/trust.js and
// section 14 of mockups/kit/surface.css: a heading that counts the entries, an export button, and a
// timeline of the record, each entry a glyph for its kind, its text, and a line with the time, how
// it was known and the citation.
//
// What is listed is the RecordEntry list and nothing else (rule 1): the session slice's record —
// every entry a record event or snapshot has carried — or, for the web app's record page, the
// entries handed in as a prop. An entry's text is shown exactly as received; the surface adds only
// the catalogue's words around it. Nothing the assistant said reaches this list.
//
// A verified entry's tag reads 'Verified on screen' and carries the legend 'reported by this
// device': the contracts define verified as attested by the user's own client, and the record must
// say so rather than let it read as independently checked.
//
// Export asks SurfaceSources.exportRecord for the walkthrough's record and saves the bytes it
// returns, untouched. The file is never built here from the entries on screen: the supervisory
// record carries a hash that ties it to the audit chain, only the server can produce it, and a file
// made in the browser would look like a record without being one. If the export fails, an error
// line says so, no file is offered and the timeline stays as it was. Nothing here logs an entry.
//
// Where this differs from the kit:
//   - The kit lists newest first and cuts the list at 60 with 'Show all'. Here the entries are in
//     the order received, as the task asks, and all of them are listed: the record is the user's
//     account of the session, and a cut-off copy of it is not.
//   - The kit's entry has a title and a detail line; RecordEntry has one text, shown as the title.
//     The kit's severity colouring has nothing to key on: RecordEntry carries no severity.
//   - The kit disables export while the record is empty, and builds the file itself. Here export is
//     offered while there is a walkthrough to export (a reference), and the file is the server's.
//   - The error line and the verified legend are new; the kit has neither.
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useStore } from "zustand";
import { useAnnounceWhen } from "../../a11y/announcer.js";
import { t } from "../../i18n/t.js";
import { Icon } from "../../icons/index.js";
import { Button } from "../../ui/button.js";
import { cn } from "../../ui/cn.js";
import { Tag } from "../../ui/tag.js";
import { CiteChip } from "../conversation/cite-chip.js";
import "./record.css";
/**
 * The glyph for each kind of entry, from the kit's KIND_ICON where the kit has the kind (a nudge is
 * the kit's tripwire, a beat its clip, navigation its navigate). The kit records no disclosure or
 * knowledge check; they take the info mark and the training mark.
 */
export const KIND_GLYPHS = {
    disclosure: "info",
    screen: "scan",
    step: "check",
    decision: "route",
    nudge: "alert",
    beat: "film",
    note: "pencil",
    refusal: "shield",
    escalation: "flag",
    navigation: "navigate",
    perception: "eye",
    "knowledge-check": "training",
};
/** The catalogue key, Tag look and glyph for each way an entry was known, as the stepper draws them. */
const HOWS = {
    verified: { key: "step.tagVerified", variant: "verified", icon: "check" },
    passed: { key: "step.tagPassed", variant: "inferred", icon: "eye" },
    acknowledged: { key: "step.tagAcknowledged", variant: "asked", icon: "user" },
    inferred: { key: "decision.tagInferred", variant: "inferred", icon: "eye" },
    asked: { key: "decision.tagAsked", variant: "asked", icon: "user" },
};
const CLOCK = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
});
/** The kit's fmtTime: the entry's time of day, on this device's clock; empty when unreadable. */
function timeOf(at) {
    const d = new Date(at);
    return Number.isNaN(d.getTime()) ? "" : CLOCK.format(d);
}
/**
 * The name the saved file takes: the walkthrough's reference, which the drafted note carries too.
 * The .json name and the application/json type in saveBytes are placeholders: nothing yet says what
 * format the server's export is, and they change when the audit module's export endpoint does.
 */
export function recordFileName(ref) {
    return `waypoint-record-${ref}.json`;
}
/**
 * Offers `bytes` to the user as a file, exactly as given: a Blob of the bytes and a download link,
 * clicked once. The link goes inside `within` (the record's own element) and is removed at once, so
 * nothing is written outside the surface's root.
 */
export function saveBytes(bytes, name, within) {
    // A copy of the view's own bytes, no more and no fewer: a Blob takes no view on a shared buffer.
    const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: "application/json" }));
    const link = within.ownerDocument.createElement("a");
    link.href = url;
    link.download = name;
    link.rel = "noopener";
    link.hidden = true;
    within.appendChild(link);
    link.click();
    link.remove();
    // The kit's grace: a browser reads the address after the click returns.
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 2000);
}
function HowTag({ how }) {
    const tag = HOWS[how];
    return (_jsx(Tag, { variant: tag.variant, icon: _jsx(Icon, { name: tag.icon, size: 12 }), "data-how": how, children: t(tag.key) }));
}
function Entry({ entry }) {
    return (_jsxs("li", { className: "wp-rec__item", "data-kind": entry.kind, "data-record-entry": "", children: [_jsx("span", { className: "wp-rec__icon", children: _jsx(Icon, { name: KIND_GLYPHS[entry.kind], size: 14 }) }), _jsxs("div", { className: "wp-rec__main", children: [_jsx("p", { className: "wp-rec__title", "data-record-text": "", children: entry.text }), _jsxs("div", { className: "wp-rec__meta", children: [_jsx("time", { dateTime: entry.at, children: timeOf(entry.at) }), entry.how === undefined ? null : _jsx(HowTag, { how: entry.how }), entry.how === "verified" ? (_jsx("span", { className: "wp-rec__legend", "data-verified-legend": "", children: t("record.verifiedLegend") })) : null, entry.citation === undefined ? null : _jsx(CiteChip, { cite: entry.citation })] })] })] }));
}
/** The record as a timeline, from entries handed in: the web app's record page and SessionRecord. */
export function RecordTimeline({ entries, walkthroughRef, sources, compact = false, className, }) {
    const titleId = useId();
    const root = useRef(null);
    const [status, setStatus] = useState("idle");
    // Said through the Announcer, assertively, when the export fails (E09-S8-T3); the line is not a
    // live region of its own.
    useAnnounceWhen(status === "failed", t("record.exportFailed"), "assertive");
    const busy = useRef(false);
    const live = useRef(true);
    useEffect(() => {
        live.current = true;
        return () => {
            live.current = false;
        };
    }, []);
    const exportRecord = useCallback(() => {
        if (walkthroughRef === null || busy.current)
            return;
        busy.current = true;
        setStatus("busy");
        sources.exportRecord(walkthroughRef).then((bytes) => {
            busy.current = false;
            if (!live.current || root.current === null)
                return;
            // Saving can fail too (no object URLs, a refused Blob): that is a failed export, not a
            // button left busy.
            try {
                saveBytes(bytes, recordFileName(walkthroughRef), root.current);
                setStatus("idle");
            }
            catch {
                setStatus("failed");
            }
        }, () => {
            busy.current = false;
            if (live.current)
                setStatus("failed");
        });
    }, [sources, walkthroughRef]);
    const unavailable = walkthroughRef === null;
    return (_jsxs("section", { ref: root, className: cn("wp-rec", compact && "wp-rec--compact", className), "aria-labelledby": titleId, "data-record": "", children: [_jsxs("header", { className: "wp-head", children: [_jsxs("div", { className: "wp-head__main", children: [_jsx("span", { className: "wp-head__icon", children: _jsx(Icon, { name: "list", size: 16 }) }), _jsxs("div", { className: "wp-head__text", children: [_jsx("h3", { id: titleId, className: "wp-head__title", children: t("record.title") }), compact ? null : _jsx("p", { className: "wp-head__sub", children: t("record.subtitle") })] })] }), _jsxs("div", { className: "wp-head__side", children: [_jsx("span", { className: "wp-count", "data-count": entries.length, children: String(entries.length) }), _jsx(Button, { compact: true, icon: _jsx(Icon, { name: "download", size: 14 }), "data-record-export": "", disabled: unavailable, "aria-disabled": status === "busy" ? true : undefined, "aria-busy": status === "busy" ? true : undefined, onClick: exportRecord, children: compact ? t("record.exportShort") : t("record.export") })] })] }), status === "failed" ? (_jsxs("p", { className: "wp-rec__error", "data-record-error": "", children: [_jsx(Icon, { name: "alert", size: 14 }), _jsx("span", { children: t("record.exportFailed") })] })) : null, entries.length > 0 ? (_jsx("ol", { className: "wp-rec__list", children: entries.map((entry, i) => (_jsx(Entry, { entry: entry }, i))) })) : (_jsxs("div", { className: "wp-empty", "data-record-empty": "", children: [_jsx(Icon, { name: "list", size: 18 }), _jsx("p", { children: t("record.empty") })] }))] }));
}
const selectRef = (s) => s.session.state?.walkthrough?.ref ?? null;
/** The session's record, kept current from the store; Export saves the current walkthrough's. */
export function SessionRecord({ store, sources, compact = false, className, }) {
    const entries = useStore(store, (s) => s.session.record);
    const walkthroughRef = useStore(store, selectRef);
    return (_jsx(RecordTimeline, { entries: entries, walkthroughRef: walkthroughRef, sources: sources, compact: compact, ...(className === undefined ? {} : { className }) }));
}
//# sourceMappingURL=record.js.map